import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { useAuth } from './AuthContext';
import { fetchWithAuth, API_ENDPOINTS } from '../config/api';

interface StripeContextType {
  loading: boolean;
  credits: number;
  createCheckoutSession: (priceId: string) => Promise<void>;
  checkCredits: () => Promise<void>;
}

const StripeContext = createContext<StripeContextType | null>(null);

export function useStripe() {
  const context = useContext(StripeContext);
  if (!context) {
    throw new Error('useStripe must be used within a StripeProvider');
  }
  return context;
}

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY!);

export function StripeProvider({ children }: { children: React.ReactNode }) {
  const [loading, setLoading] = useState(false);
  const [credits, setCredits] = useState(0);
  const { currentUser } = useAuth();

  const checkCredits = useCallback(async () => {
    if (!currentUser) return;

    try {
      const response = await fetchWithAuth(API_ENDPOINTS.CREDITS(currentUser.uid));
      const data = await response.json();
      setCredits(data.credits);
    } catch (error) {
      console.error('Error checking credits:', error);
    }
  }, [currentUser]);

  // Fetch credits when the component mounts and when currentUser changes
  useEffect(() => {
    if (currentUser) {
      checkCredits();
    }
  }, [currentUser, checkCredits]);

  async function createCheckoutSession(priceId: string) {
    if (!currentUser) {
      throw new Error('Must be logged in to purchase credits');
    }

    setLoading(true);
    try {
      const token = await currentUser.getIdToken();
      const response = await fetchWithAuth(API_ENDPOINTS.CREATE_CHECKOUT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({
          priceId,
          userId: currentUser.uid
        })
      });

      const { sessionId } = await response.json();
      const stripe = await stripePromise;
      
      if (!stripe) {
        throw new Error('Stripe failed to load');
      }

      const { error } = await stripe.redirectToCheckout({ sessionId });
      
      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }

  const value = {
    loading,
    credits,
    createCheckoutSession,
    checkCredits
  };

  return (
    <StripeContext.Provider value={value}>
      {children}
    </StripeContext.Provider>
  );
}
